const moment = require('moment-timezone');
const {getLineItemSortIndex} = require('./lineItemPriorities');

// Number formatter, shortened to nf
// API returns some values INFINITY, or NaN (as strings), so we need to check if the value is a number else return 0
const nf = (value, metric) => {
  if (metric === 'date') {
    return value;
  }
  if (value === undefined || value === null) {
    return 0;
  }

  // Handle string "NaN" or "Infinity"
  if (
    typeof value === 'string' &&
    (value.toLowerCase() === 'nan' || value.toLowerCase() === 'infinity')
  ) {
    return 0;
  }

  const parsed = parseFloat(value);
  if (!isFinite(parsed) || isNaN(parsed)) {
    return 0;
  }

  return parsed;
};

// Global site impressions (API key); merged graph rows expose the same value as `requests` from fixed metrics
const dayAllImp = d => nf(d['All Imp'] ?? d.requests);

const dayProgrammaticEligibleRequests = d =>
  nf(d['Programmatic Eligible Requests'] ?? d.programmaticEligibleRequests);

// Day-level denominators used in calculated formulas (not per-source)
const DAY_LEVEL_DENOMINATOR_FIELDS = {
  'All Imp': dayAllImp,
  'Programmatic Eligible Requests': dayProgrammaticEligibleRequests,
};

const isDayLevelDenominatorField = field => field in DAY_LEVEL_DENOMINATOR_FIELDS;

const dayLevelDenominatorValue = (day, field) => {
  const getter = DAY_LEVEL_DENOMINATOR_FIELDS[field];
  return getter ? getter(day) : null;
};

// Helper to calculate average for non-cumulative metrics
// For cumulative metrics, we sum values
// For non-cumulative metrics (like rates), we need to average them
const calculateAverage = (sum, count) => {
  return count > 0 ? sum / count : 0;
};

const normalizeSourceKey = source => String(source || '').toLowerCase().trim();
const normalizeSourceKeyWithoutS2S = source => normalizeSourceKey(source).replace(/s2s$/, '');
const isDirectSource = (source, directSourcesSet = new Set()) => {
  const normalized = normalizeSourceKey(source);
  const normalizedWithoutS2S = normalizeSourceKeyWithoutS2S(source);
  return directSourcesSet.has(normalized) || directSourcesSet.has(normalizedWithoutS2S);
};

/** ADX / Open Bidding rows added alongside prebid bidders — not subject to prebid reseller commission. */
const NON_PREBID_SSP_SOURCES = new Set(['google', 'openbidding']);

const isNetResellerRevenueSource = (source, stackType, directSourcesSet = new Set()) => {
  if (stackType === 'demand') {
    return source === 'Prebid Reseller';
  }

  if (isDirectSource(source, directSourcesSet)) return false;
  if (NON_PREBID_SSP_SOURCES.has(normalizeSourceKey(source))) return false;
  return true;
};

const applyNetPrebidResellerRevenue = (data, directSourcesSet, commissionPercent, stackType = 'prebid') => {
  if (!Array.isArray(data) || !data.length || commissionPercent == null || commissionPercent === '') {
    return data;
  }

  const multiplier = 1 - Number(commissionPercent) / 100;
  if (!Number.isFinite(multiplier) || multiplier === 1) return data;

  return data.map(day => {
    const nextDay = {...day};

    Object.keys(day).forEach(key => {
      if (!key.endsWith(' Rev')) return;

      const source = key.slice(0, -' Rev'.length);
      if (!isNetResellerRevenueSource(source, stackType, directSourcesSet)) return;

      const grossRev = nf(day[key]);
      const netRev = grossRev * multiplier;
      nextDay[key] = netRev.toFixed(2);

      const imp = nf(day[`${source} Paid Imp`]);
      nextDay[`${source} eCPM`] = imp > 0 ? ((netRev / imp) * 1000).toFixed(2) : '0.00';
    });

    return nextDay;
  });
};

const SPLIT_MODE_CONFIG = {
  serverClient: {
    primaryDataKey: 'graphDataNonS2S',
    secondaryDataKey: 'graphDataS2S',
    primaryLabel: 'Prebid Client',
    secondaryLabel: 'Prebid Server',
    primarySuffix: ' Non-S2S',
    secondarySuffix: ' S2S',
    primaryBucket: 'Non-S2S',
    secondaryBucket: 'S2S',
  },
  directReseller: {
    primaryDataKey: 'graphDataReseller',
    secondaryDataKey: 'graphDataDirect',
    primaryLabel: 'Reseller',
    secondaryLabel: 'Direct',
    primarySuffix: ' Reseller',
    secondarySuffix: ' Direct',
    primaryBucket: 'Reseller',
    secondaryBucket: 'Direct',
  },
};

const getSplitModeConfig = splitMode => SPLIT_MODE_CONFIG[splitMode] || SPLIT_MODE_CONFIG.serverClient;

const getSourceSplitBucket = (source, splitMode = 'serverClient', directSourcesSet = new Set()) => {
  if (splitMode === 'directReseller') {
    return isDirectSource(source, directSourcesSet) ? 'Direct' : 'Reseller';
  }
  return source.toLowerCase().includes('s2s') ? 'S2S' : 'Non-S2S';
};

const sourceBelongsToMode = (source, mode = 'grouped', splitMode = 'serverClient', directSet) => {
  if (mode === 'grouped') return true;
  return getSourceSplitBucket(source, splitMode, directSet) === mode;
};

const getAvailableSources = (data, sourcesConfig, metricsConfig) => {
  let getAvailableSources = [];

  if (!data || !data.length) return [];

  // Find first cumulative metric to use for source detection
  const firstCumulativeMetric = Object.values(metricsConfig.perSource).find(
    metric => metric.cumulative
  );
  const detectionField =
    firstCumulativeMetric?.dataField || Object.values(metricsConfig.perSource)[0]?.dataField;

  if (!detectionField) return [];

  // For dynamic sources (prebid, analytics), sourcesConfig might be empty initially
  // Extract sources directly from data structure
  if (Object.keys(sourcesConfig).length === 0) {
    // Extract sources from data keys (format: "Source MetricName")
    data.forEach(day => {
      Object.keys(day)
        .filter(key => key !== 'date' && !key.startsWith('enabledSources_'))
        .forEach(key => {
          const source = key.split(' ')[0];
          if (
            day[key] !== undefined &&
            day[key] !== null &&
            !getAvailableSources.includes(source)
          ) {
            getAvailableSources.push(source);
          }
        });
    });
  } else {
    // Use sourcesConfig keys for static sources (demand, direct)
    data.forEach(day => {
      Object.keys(sourcesConfig).forEach(source => {
        if (
          day[`${source} ${detectionField}`] !== undefined &&
          day[`${source} ${detectionField}`] !== null
        ) {
          if (!getAvailableSources.includes(source)) {
            getAvailableSources.push(source);
          }
        }
      });
    });
  }
  return getAvailableSources;
};

let getAvailableSourcesSorted = (
  metricsConfig,
  totals,
  availableSources,
  sortByKey,
  sortOrder,
  reportType = null
) => {
  // Special sorting for direct reports by line item priority when sorting by demand source
  if (reportType === 'direct' && sortByKey === 'Demand Source Priority') {
    return [...availableSources].sort((a, b) => {
      const aIndex = getLineItemSortIndex(a);
      const bIndex = getLineItemSortIndex(b);

      return sortOrder === 'asc' ? aIndex - bIndex : bIndex - aIndex;
    });
  }

  const metricConfig = Object.values(metricsConfig.perSource).find(
    config => config.dataField === sortByKey
  );

  const sortField = metricConfig?.dataField;
  if (!sortField) return [...availableSources];

  return [...availableSources].sort((a, b) => {
    const aValue = parseFloat(totals?.perSource?.[`${a} ${sortField}`]) ?? 0;
    const bValue = parseFloat(totals?.perSource?.[`${b} ${sortField}`]) ?? 0;

    return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
  });
};

// Returns day data in a structured format for the graph for fixed metrics
const getStructuredFixedGraphDataForTheDay = (day, metricsConfig) => {
  let metrics = {};
  Object.keys(metricsConfig.fixed).forEach(metric => {
    metrics[metric] = nf(day[metricsConfig.fixed[metric].dataField], metric);
  });
  return metrics;
};

// Returns day data in a structured format for the graph by the available sources
const getStructuredDynamicGraphDataForTheDay = (
  day,
  availableSources,
  metricsConfig,
  disabledSources,
  // modes: grouped, S2S, Non-S2S
  mode,
  splitMode = 'serverClient',
  directSourcesSet = new Set()
) => {
  // Get list of calculated metric dataFields to skip them in perSource processing
  const calculatedDataFields = metricsConfig.calculated
    ? Object.values(metricsConfig.calculated).map(config => config.dataField)
    : [];

  const result = availableSources.reduce((metrics, source) => {
    if (!sourceBelongsToMode(source, mode, splitMode, directSourcesSet)) return metrics;

    Object.keys(metricsConfig.perSource).forEach(metric => {
      let config = metricsConfig.perSource[metric];

      // Skip calculated metrics - they'll be calculated separately
      if (calculatedDataFields.includes(config.dataField)) {
        return;
      }

      let key = `${source} ${config.dataField}`;

      if (metrics[config.dataField] === undefined) {
        metrics[config.dataField] = 0;
        metrics[key] = 0;
      }

      const weightField = config.averageWeightField;
      const weightValue = weightField ? nf(day[`${source} ${weightField}`]) : 1;
      const safeWeight = weightValue > 0 ? weightValue : 0;
      const metricValue = nf(day[key]);

      if (!disabledSources.includes(source)) {
        // Track enabled sources totals/averages for all metrics
        const enabledKey = `enabledSources_${config.dataField}`;
        const enabledCountKey = `_count_enabledSources_${config.dataField}`;

        if (metrics[enabledKey] === undefined) {
          metrics[enabledKey] = 0;
          if (!config.cumulative) {
            metrics[enabledCountKey] = 0;
          }
        }

        if (config.cumulative) {
          // For cumulative metrics, sum them up
          metrics[enabledKey] += metricValue;
        } else {
          // For non-cumulative metrics, weight sums for averaging
          metrics[enabledKey] += metricValue * (safeWeight || 1);
          metrics[enabledCountKey] += safeWeight || 1;
        }
      }

      metrics[config.dataField] += metricValue;
      metrics[key] = metricValue;
    });

    return metrics;
  }, {});

  // Day-level-only metrics (e.g. demand Ad Requests ← Programmatic Eligible Requests)
  // are not available per SSP — copy the day total onto the metric + enabledSources keys.
  Object.keys(metricsConfig.perSource).forEach(metric => {
    const config = metricsConfig.perSource[metric];
    if (!config.dayLevelOnly && !isDayLevelDenominatorField(config.dataField)) {
      return;
    }
    if (day[config.dataField] == null && day.programmaticEligibleRequests == null) {
      return;
    }
    const dayVal = dayLevelDenominatorValue(day, config.dataField);
    if (dayVal === null) return;
    result[config.dataField] = dayVal;
    result[`enabledSources_${config.dataField}`] = dayVal;
  });

  // Calculate derived metrics (like eCPM) AFTER all sources are processed
  // This ensures enabledSources values are fully summed before calculation
  if (metricsConfig.calculated) {
    Object.keys(metricsConfig.calculated).forEach(calcKey => {
      const calcConfig = metricsConfig.calculated[calcKey];
      if (calcConfig.formula) {
        // Parse simple formula: "Rev / Paid Imp * 1000"
        const parts = calcConfig.formula.split('/');
        if (parts.length === 2) {
          const numerator = parts[0].trim();
          const denominator = parts[1].split('*')[0].trim();
          const multiplier = parts[1].includes('*') ? parseFloat(parts[1].split('*')[1].trim()) : 1;

          const enabledVal = f => {
            const dayLevel = dayLevelDenominatorValue(day, f);
            if (dayLevel !== null) return dayLevel;
            return result[`enabledSources_${f}`];
          };
          const sourceVal = (f, src) => {
            const dayLevel = dayLevelDenominatorValue(day, f);
            if (dayLevel !== null) return dayLevel;
            return result[`${src} ${f}`];
          };

          if (enabledVal(numerator) !== undefined && enabledVal(denominator) !== 0) {
            result[`enabledSources_${calcConfig.dataField}`] = nf(
              (enabledVal(numerator) / enabledVal(denominator)) * multiplier
            );
          }

          availableSources.forEach(source => {
            if (!sourceBelongsToMode(source, mode, splitMode, directSourcesSet)) return;

            if (
              sourceVal(numerator, source) !== undefined &&
              sourceVal(denominator, source) !== 0
            ) {
              result[`${source} ${calcConfig.dataField}`] = nf(
                (sourceVal(numerator, source) / sourceVal(denominator, source)) * multiplier
              );
            }
          });
        }
      }
    });
  }

  // Calculate averages for non-cumulative metrics
  // Skip calculated metrics as they're already calculated correctly
  Object.keys(metricsConfig.perSource).forEach(metric => {
    const config = metricsConfig.perSource[metric];
    // Don't average calculated metrics - they're already calculated correctly
    if (!config.cumulative && !calculatedDataFields.includes(config.dataField)) {
      const enabledKey = `enabledSources_${config.dataField}`;
      const enabledCountKey = `_count_enabledSources_${config.dataField}`;
      if (result[enabledCountKey] > 0) {
        result[enabledKey] = result[enabledKey] / result[enabledCountKey];
      }
      // Remove the count key as it's only used for calculation
      delete result[enabledCountKey];
    }
  });

  return result;
};

// Returns totals by the available sources from daily data
const getTotals = (
  graphData,
  availableSources,
  metricsConfig,
  disabledSources = [],
  splitMode = 'serverClient',
  directSourcesSet = new Set()
) => {
  const calculatedDataFields = metricsConfig.calculated
    ? Object.values(metricsConfig.calculated).map(config => config.dataField)
    : [];

  let totals = {
    perSource: {},
  };

  // Track counts (or weights) for averaging non-cumulative metrics
  let counts = {
    perSource: {},
  };

  // Pre-initialize all sources with 0 values for all metrics
  availableSources.forEach(source => {
    Object.keys(metricsConfig.perSource).forEach(metric => {
      let config = metricsConfig.perSource[metric];
      let key = `${source} ${config.dataField}`;
      totals.perSource[key] = 0;
      if (!config.cumulative) {
        counts.perSource[key] = 0;
      }
    });
  });

  // Pre-initialize totals for all metrics
  Object.keys(metricsConfig.perSource).forEach(metric => {
    let config = metricsConfig.perSource[metric];
    totals[config.dataField] = 0;
    if (!config.cumulative) {
      counts[config.dataField] = 0;
    }
  });

  graphData.map(day => {
    Object.keys(DAY_LEVEL_DENOMINATOR_FIELDS).forEach(field => {
      totals[field] = (totals[field] || 0) + DAY_LEVEL_DENOMINATOR_FIELDS[field](day);
    });
    availableSources.map(source => {
      if (disabledSources.includes(source)) return null;

      Object.keys(metricsConfig.perSource).forEach(metric => {
        let config = metricsConfig.perSource[metric];

        if (calculatedDataFields.includes(config.dataField)) {
          return;
        }

        // Day-level metrics are summed via DAY_LEVEL_DENOMINATOR_FIELDS above
        if (config.dayLevelOnly || isDayLevelDenominatorField(config.dataField)) {
          return;
        }

        let key = `${source} ${config.dataField}`;

        const weightField = config.averageWeightField;
        const weightValue = weightField ? nf(day[`${source} ${weightField}`]) : 1;
        const safeWeight = weightValue > 0 ? weightValue : 0;
        let value = nf(day[key]);

        // For cumulative metrics, sum them up
        // For non-cumulative metrics (like rates), calculate averages
        if (config.cumulative) {
          // Set total metric by source
          totals[`perSource`][key] += value;

          // Total by metric
          // Initialize the total for the metric
          if (!totals[config.dataField]) {
            totals[config.dataField] = 0;
          }

          // Set total for the metric
          totals[config.dataField] += value;

          // Calculate S2S and Non-S2S totals
          const splitLabel = getSourceSplitBucket(source, splitMode, directSourcesSet);
          let keyUngrouped = `${config.dataField} ${splitLabel}`;

          // Total by metric ungrouped
          // Initialize the total for the metric ungrouped
          if (!totals[keyUngrouped]) {
            totals[keyUngrouped] = 0;
          }

          // Set total for the metric
          totals[keyUngrouped] += value;
        } else {
          // For non-cumulative metrics, sum values and count occurrences for averaging
          // Initialize per-source total if needed
          if (totals.perSource[key] === undefined) {
            totals.perSource[key] = 0;
            counts.perSource[key] = 0;
          }
          totals.perSource[key] += value * (safeWeight || 1);
          counts.perSource[key] += safeWeight || 1;

          // Initialize total for the metric if needed
          if (!totals[config.dataField]) {
            totals[config.dataField] = 0;
            counts[config.dataField] = 0;
          }
          totals[config.dataField] += value * (safeWeight || 1);
          counts[config.dataField] += safeWeight || 1;

          // Calculate S2S and Non-S2S totals for non-cumulative metrics too
          const splitLabel = getSourceSplitBucket(source, splitMode, directSourcesSet);
          let keyUngrouped = `${config.dataField} ${splitLabel}`;

          if (!totals[keyUngrouped]) {
            totals[keyUngrouped] = 0;
            counts[keyUngrouped] = 0;
          }
          totals[keyUngrouped] += value * (safeWeight || 1);
          counts[keyUngrouped] += safeWeight || 1;
        }
      });
    });
  });

  // Calculate averages for non-cumulative metrics
  Object.keys(metricsConfig.perSource).forEach(metric => {
    let config = metricsConfig.perSource[metric];
    if (!config.cumulative && !calculatedDataFields.includes(config.dataField)) {
      // Average per-source totals
      availableSources.forEach(source => {
        let key = `${source} ${config.dataField}`;
        if (counts.perSource[key] > 0) {
          totals.perSource[key] = totals.perSource[key] / counts.perSource[key];
        }
      });

      // Average total for the metric
      if (counts[config.dataField] > 0) {
        totals[config.dataField] = totals[config.dataField] / counts[config.dataField];
      }

      // Average S2S and Non-S2S totals
      const splitConfig = getSplitModeConfig(splitMode);
      const splitAKey = `${config.dataField} ${splitConfig.secondaryBucket}`;
      const splitBKey = `${config.dataField} ${splitConfig.primaryBucket}`;
      if (counts[splitAKey] > 0) {
        totals[splitAKey] = totals[splitAKey] / counts[splitAKey];
      }
      if (counts[splitBKey] > 0) {
        totals[splitBKey] = totals[splitBKey] / counts[splitBKey];
      }
    }
  });

  // Calculate derived metrics (like eCPM) if configured
  if (metricsConfig.calculated) {
    Object.keys(metricsConfig.calculated).forEach(calcKey => {
      const calcConfig = metricsConfig.calculated[calcKey];

      if (calcConfig.formula) {
        // Parse simple formula: "Rev / Paid Imp * 1000"
        const parts = calcConfig.formula.split('/');
        if (parts.length === 2) {
          const numerator = parts[0].trim();
          const denominator = parts[1].split('*')[0].trim();
          const multiplier = parts[1].includes('*') ? parseFloat(parts[1].split('*')[1].trim()) : 1;

          const totDen = totals[denominator];
          if (totals[numerator] !== undefined && totDen !== undefined && totDen !== 0) {
            totals[calcConfig.dataField] = nf((totals[numerator] / totDen) * multiplier);
          }

          const splitConfig = getSplitModeConfig(splitMode);
          const splitALabel = splitConfig.secondaryBucket;
          const splitBLabel = splitConfig.primaryBucket;
          const numeratorA = `${numerator} ${splitALabel}`;
          const denominatorA = `${denominator} ${splitALabel}`;
          const denA = isDayLevelDenominatorField(denominator)
            ? totals[denominator]
            : totals[denominatorA];
          if (totals[numeratorA] && denA && denA !== 0) {
            totals[`${calcConfig.dataField} ${splitALabel}`] = nf(
              (totals[numeratorA] / denA) * multiplier
            );
          }

          const numeratorB = `${numerator} ${splitBLabel}`;
          const denominatorB = `${denominator} ${splitBLabel}`;
          const denB = isDayLevelDenominatorField(denominator)
            ? totals[denominator]
            : totals[denominatorB];
          if (totals[numeratorB] && denB && denB !== 0) {
            totals[`${calcConfig.dataField} ${splitBLabel}`] = nf(
              (totals[numeratorB] / denB) * multiplier
            );
          }

          availableSources.forEach(source => {
            const sourceNumerator = `${source} ${numerator}`;
            const sourceDenominator = `${source} ${denominator}`;
            const denSrc = isDayLevelDenominatorField(denominator)
              ? totals[denominator]
              : totals.perSource[sourceDenominator];

            if (
              totals.perSource[sourceNumerator] &&
              denSrc &&
              denSrc !== 0
            ) {
              totals.perSource[`${source} ${calcConfig.dataField}`] = nf(
                (totals.perSource[sourceNumerator] / denSrc) * multiplier
              );
            }
          });
        }
      }
    });
  }

  return totals;
};

// Returns percents, first param is value, another is sum
const getPercents = (value, sum) => {
  if (sum === 0) {
    return 0;
  }

  return ((value / sum) * 100).toFixed(2);
};

const getMaxValueForGraph = (data, metricsConfig, sourcesConfig) => {
  let max = 0;

  data.forEach(day => {
    if (metricsConfig.dayLevelOnly) {
      const value =
        day[`enabledSources_${metricsConfig.dataField}`] ?? day[metricsConfig.dataField] ?? 0;
      if (value > max) {
        max = value;
      }
      return;
    }

    if (!metricsConfig.cumulative) {
      Object.keys(sourcesConfig).forEach(source => {
        let key = `${source} ${metricsConfig.dataField}`;
        let value = day[key];
        if (value > max) {
          max = value;
        }
      });
    } else {
      let key = `${metricsConfig.dataField}`;
      let value = day[key];
      if (value > max) {
        max = value;
      }
    }
  });

  return max;
};

const sortedDaysData = (daysData, sortOrder) => {
  return Object.fromEntries(
    Object.entries(daysData).sort(([, a], [, b]) => {
      const aVal = parseFloat(a);
      const bVal = parseFloat(b);
      return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    })
  );
};

// Convert analytics UTC date strings to local timezone for proper hour display
// Keep store period dates as YYYY-MM-DD. Analytics APIs need UTC instants for Today.
const toAnalyticsPeriod = (period, realtimeMinutes) => {
  if (!period) return period;
  if (period.label === 'realtime') {
    return {...period, realtimeMinutes: realtimeMinutes ?? 10};
  }
  if (period.label === 'Today') {
    const timezone = period.timezone || moment.tz.guess();
    const start = moment.tz(String(period.startDate).slice(0, 10), timezone).startOf('day');
    return {
      ...period,
      startDate: start.clone().utc().toISOString(),
      endDate: start.clone().add(1, 'day').utc().toISOString(),
    };
  }
  return period;
};

const convertAnalyticsDatesToLocalTimezone = (data, period) => {
  if (!data || !Array.isArray(data)) return data;

  const shouldNormalizeToHour =
    period?.label === 'Last 24 hours' || period?.label === 'realtime' || period?.label === 'Today';
  const dateFormat = shouldNormalizeToHour ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD';

  return data.map(item => {
    if (!item.date) return item;
    const parsed = period?.timezone
      ? moment.utc(item.date).tz(period.timezone)
      : moment(item.date).local();
    return {
      ...item,
      date: parsed.format(dateFormat),
    };
  });
};

// Fill in missing dates (daily) or hours (Last 24 hours) or seconds (realtime) in the data array
const fillMissingDates = (data, period = null) => {
  if (!data?.length) return data;

  // Detect if data points include time (YYYY-MM-DD HH:mm), used for Last 24 hours
  const hasTime = data.some(d => typeof d.date === 'string' && d.date.includes(':'));
  const isRealtime = period?.label === 'realtime';

  let unit, format, step;
  if (isRealtime) {
    // For realtime data with 15-second intervals
    unit = 'seconds';
    format = 'YYYY-MM-DD HH:mm:ss';
    step = 15; // 15-second intervals
  } else if (hasTime) {
    // For Last 24 hours (hourly data)
    unit = 'hours';
    format = 'YYYY-MM-DD HH:mm:ss';
    step = 1;
  } else {
    // Daily data
    unit = 'days';
    format = 'YYYY-MM-DD';
    step = 1;
  }

  // Sort data by date to ensure correct order (oldest first)
  const sortedData = [...data].sort((a, b) => moment(a.date).diff(moment(b.date)));

  let start = moment(sortedData[0].date);
  let end = moment(sortedData[sortedData.length - 1].date);

  if (period?.label === 'Today' && unit === 'hours') {
    start = start.clone().startOf('day');
    const currentHour = moment().startOf('hour');
    if (currentHour.isAfter(end)) {
      end = currentHour;
    }
  }

  let span;
  if (isRealtime) {
    // Calculate span in seconds, then divide by step (15)
    // Timestamps are always at exact 15-second boundaries (00, 15, 30, 45)
    const totalSeconds = end.diff(start, 'seconds');
    span = Math.floor(totalSeconds / step) + 1;
  } else {
    span = end.diff(start, unit) + 1;
  }

  const allDates = [...Array(span)].map((_, i) => {
    const dateStr = start
      .clone()
      .add(i * step, unit)
      .format(format);
    return {date: dateStr};
  });

  // Match existing data points (exact match for all periods since timestamps are at exact boundaries)
  return allDates.map(day => {
    return sortedData.find(d => d.date === day.date) || day;
  });
};

// Returns the display name for a source, using displayNameOverride if available
const getDisplayName = (source, sourcesConfig) => {
  return (
    sourcesConfig[source]?.displayNameOverride ||
    source.replace(/S2S/i, ' S2S').replace(/_/i, ' ')
  );
};

// Returns the sorted sources for a specific day
const getSortedSourcesForDay = (
  dayDataContext,
  availableSources,
  metricsConfig,
  enabledMetric,
  disabledSources = [],
  sortOrder = 'desc',
  sortByKey = null,
  // dataProviderKey is used to distinguish between grouped, S2S and Non-S2S datasets
  // so that tooltips only show sources that actually contribute to the active split.
  dataProviderKey = 'graphData',
  splitMode = 'serverClient',
  directSourcesSet = new Set()
) => {
  const sourcesWithValues = [];

  // Check if enabled metric is a calculated metric
  let enabledMetricConfig = null;
  let isCalculatedMetric = false;

  // First check perSource
  const perSourceKey = Object.keys(metricsConfig.perSource).find(
    key => metricsConfig.perSource[key].dataField === enabledMetric
  );
  if (perSourceKey) {
    enabledMetricConfig = metricsConfig.perSource[perSourceKey];
  } else if (metricsConfig.calculated) {
    // Check calculated metrics
    const calculatedKey = Object.keys(metricsConfig.calculated).find(
      key => metricsConfig.calculated[key].dataField === enabledMetric
    );
    if (calculatedKey) {
      enabledMetricConfig = metricsConfig.calculated[calculatedKey];
      isCalculatedMetric = true;
    }
  }

  if (!enabledMetricConfig) {
    return sourcesWithValues;
  }

  availableSources.forEach(source => {
    const splitConfig = getSplitModeConfig(splitMode);
    const modeByDataProvider = {
      graphData: 'grouped',
      [splitConfig.primaryDataKey]: splitConfig.primaryBucket,
      [splitConfig.secondaryDataKey]: splitConfig.secondaryBucket,
    };
    const mode = modeByDataProvider[dataProviderKey] || 'grouped';
    if (!sourceBelongsToMode(source, mode, splitMode, directSourcesSet)) return;

    if (disabledSources.includes(source)) {
      return;
    }

    let value = 0;
    let totalValue = 0;

    if (isCalculatedMetric) {
      // For calculated metrics, get per-source calculated value
      value = dayDataContext[`${source} ${enabledMetricConfig.dataField}`] || 0;
      // Use enabledSources value as totalValue for calculated metrics
      totalValue = dayDataContext[`enabledSources_${enabledMetricConfig.dataField}`] || 0;
    } else {
      // For regular metrics, get per-source value
      value = dayDataContext[`${source} ${enabledMetricConfig.dataField}`] || 0;
      totalValue = dayDataContext[enabledMetricConfig.dataField] || 0;
    }

    sourcesWithValues.push({
      source,
      config: enabledMetricConfig,
      value: parseFloat(value) || 0,
      totalValue: parseFloat(totalValue) || 0,
      metric: enabledMetricConfig.dataField,
    });
  });

  // Special sorting for Demand Source Priority
  if (sortByKey === 'Demand Source Priority') {
    sourcesWithValues.sort((a, b) => {
      const aIndex = getLineItemSortIndex(a.source);
      const bIndex = getLineItemSortIndex(b.source);
      return sortOrder === 'asc' ? aIndex - bIndex : bIndex - aIndex;
    });
  } else {
    // Sort by value according to sortOrder
    sourcesWithValues.sort((a, b) => {
      return sortOrder === 'asc' ? a.value - b.value : b.value - a.value;
    });
  }

  return sourcesWithValues;
};

export {
  getSplitModeConfig,
  getSourceSplitBucket,
  getStructuredFixedGraphDataForTheDay,
  getAvailableSources,
  getAvailableSourcesSorted,
  getStructuredDynamicGraphDataForTheDay,
  getTotals,
  getPercents,
  getMaxValueForGraph,
  sortedDaysData,
  fillMissingDates,
  convertAnalyticsDatesToLocalTimezone,
  toAnalyticsPeriod,
  applyNetPrebidResellerRevenue,
  isNetResellerRevenueSource,
  nf,
  getDisplayName,
  getSortedSourcesForDay,
};
